import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Sparkles, X, Check, Loader2, Crown } from "lucide-react";
import { api } from "@/lib/pookieApi";
import { getDeviceId, getProEmail, setProEmail } from "@/lib/subscription";
import { openRazorpay } from "@/lib/razorpay";
import { useAuth } from "@/lib/AuthContext";
import { toast } from "sonner";

export default function PaywallModal({ open, onClose, freeUsed = 0, onPaid }) {
  const [email, setEmail] = useState(getProEmail() || "");
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(false);
  const nav = useNavigate();
  const { user } = useAuth();

  useEffect(() => {
    if (!open) return;
    api.get("/plans").then((r) => setPlan((r.data.plans || [])[0])).catch(() => {});
  }, [open]);

  const onUpgrade = async () => {
    if (!email || !email.includes("@")) {
      toast.error("Pookie, we need a real email to send your receipt.");
      return;
    }
    setLoading(true);
    try {
      const { data: order } = await api.post("/checkout/order", {
        plan_id: plan?.id || "pookie-pro-monthly",
        email,
        device_id: getDeviceId(),
      });
      setProEmail(email);

      await openRazorpay(order, {
        email,
        contact: user?.phone || "",
        onSuccess: async (resp) => {
          try {
            await api.post("/checkout/verify", {
              razorpay_order_id: resp.razorpay_order_id,
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_signature: resp.razorpay_signature,
            });
            toast.success("You're Pro, pookie. Welcome.");
            onPaid && onPaid();
            onClose && onClose();
            nav(`/pay/success?order_id=${resp.razorpay_order_id}`);
          } catch (e) {
            toast.error("Payment verification failed. Contact support, pookie.");
          } finally {
            setLoading(false);
          }
        },
        onDismiss: (err) => {
          setLoading(false);
          if (err) toast.error(err.description || "Payment cancelled.");
        },
      });
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.detail || e?.message || "Checkout could not be created.");
      setLoading(false);
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 grid place-items-center px-4 bg-black/60 backdrop-blur-sm"
      data-testid="paywall-modal"
      onClick={(e) => e.target === e.currentTarget && onClose && onClose()}
    >
      <div className="pl-card bg-white max-w-lg w-full p-6 md:p-8 relative">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 w-9 h-9 grid place-items-center rounded-full border-[3px] border-black bg-white hover:bg-[#FFE600]"
          data-testid="paywall-close"
          aria-label="Close"
        >
          <X size={18} strokeWidth={3} />
        </button>

        <div className="pl-chip bg-[#FFE600] mb-4">
          <Crown size={14} strokeWidth={3} /> Pookie Pro
        </div>
        <h2 className="font-display text-3xl md:text-4xl tracking-tighter leading-tight">
          You've used your {freeUsed || 2} free verdicts, pookie.
        </h2>
        <p className="font-mono-pl text-sm mt-3 text-black/75">
          Judge Pookie's docket is full. Upgrade to keep the court in session and prosecute infinite nonsense.
        </p>

        <div className="pl-card-flat bg-[#B4F8C8] p-5 mt-6">
          <div className="flex items-baseline gap-2">
            <span className="font-display text-5xl">₹{plan?.amount || 99}</span>
            <span className="font-mono-pl text-sm">/ month</span>
          </div>
          <ul className="mt-3 space-y-1 font-mono-pl text-sm">
            {[
              "Unlimited AI verdicts",
              "Share verdict cards everywhere",
              "Early access to new categories",
              "Cancel anytime, no shade",
            ].map((f) => (
              <li key={f} className="flex items-center gap-2">
                <Check size={14} strokeWidth={3} /> {f}
              </li>
            ))}
          </ul>
        </div>

        <label className="pl-label mt-6 block">Email for receipt</label>
        <input
          data-testid="paywall-email"
          className="pl-input mt-2"
          type="email"
          placeholder="pookie@example.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />

        <button
          data-testid="paywall-upgrade"
          onClick={onUpgrade}
          disabled={loading}
          className="pl-btn pl-btn-primary w-full justify-center mt-5"
        >
          {loading ? (
            <>
              <Loader2 className="animate-spin" size={16} /> Opening Razorpay…
            </>
          ) : (
            <>
              <Sparkles size={16} strokeWidth={3} /> Pay ₹{plan?.amount || 99} with Razorpay
            </>
          )}
        </button>

        <p className="font-mono-pl text-[11px] text-black/55 mt-3 text-center">
          Secure checkout by Razorpay. Test mode — use card 4111 1111 1111 1111.
        </p>
      </div>
    </div>
  );
}
